/**
 * Живые обновления: подписка на поток событий сервера.
 *
 * Одно соединение на вкладку, к которому подключаются все компоненты.
 * Браузер сам переподключается при обрыве, поэтому логика повторов здесь
 * не нужна — достаточно не открывать десять соединений разом.
 */

import { useEffect, useRef, useState } from 'react';

export type LiveEvent =
  | { type: 'connected' }
  | { type: 'event.changed'; eventId: number }
  | { type: 'event.created'; eventId: number }
  | { type: 'events.changed' }
  | { type: 'application.changed'; applicationId: number }
  | { type: 'players.changed' }
  | { type: 'rollbacks.changed' }
  | { type: 'settings.changed' };

type Listener = (event: LiveEvent) => void;

const listeners = new Set<Listener>();
let source: EventSource | null = null;
let connected = false;
const connectionListeners = new Set<(value: boolean) => void>();

/** Открывать ли поток вообще. Выключается для уровня «игрок». */
let enabled = true;

function setConnected(value: boolean): void {
  if (connected === value) {
    return;
  }

  connected = value;
  for (const listener of connectionListeners) {
    listener(value);
  }
}

/**
 * Включает или выключает живые обновления для всей вкладки.
 *
 * Выключается для уровня «игрок»: статистику смотрит вся семья, и сотня
 * постоянных соединений после капта — не та нагрузка, ради которой стоит
 * обновлять цифры, меняющиеся раз в вечер. Управлению поток нужен: там
 * наборы и заявки живут минутами.
 */
export function setLiveUpdates(value: boolean): void {
  if (enabled === value) {
    return;
  }

  enabled = value;

  if (!enabled && source !== null) {
    source.close();
    source = null;
    setConnected(false);
  }
}

/** Открыт ли поток обновлений — по нему прячется значок «В эфире». */
export function liveUpdatesEnabled(): boolean {
  return enabled;
}

function ensureConnection(): void {
  if (source !== null || !enabled) {
    return;
  }

  source = new EventSource('/api/stream');

  source.addEventListener('open', () => {
    setConnected(true);
  });

  source.addEventListener('error', () => {
    setConnected(false);
  });

  source.addEventListener('message', (message) => {
    try {
      const parsed = JSON.parse(message.data as string) as LiveEvent;
      setConnected(true);
      for (const listener of listeners) {
        listener(parsed);
      }
    } catch {
      // Повреждённое сообщение просто пропускаем.
    }
  });
}

function closeIfUnused(): void {
  if (listeners.size === 0 && source !== null) {
    source.close();
    source = null;
    setConnected(false);
  }
}

/** Подписка на события сервера. */
export function useLiveEvents(listener: Listener): void {
  const stored = useRef(listener);
  stored.current = listener;

  useEffect(() => {
    const wrapper: Listener = (event) => {
      stored.current(event);
    };

    listeners.add(wrapper);
    ensureConnection();

    return () => {
      listeners.delete(wrapper);
      closeIfUnused();
    };
  }, []);
}

/** Состояние соединения — показывается в шапке. */
export function useLiveConnection(): boolean {
  const [value, setValue] = useState(connected);

  useEffect(() => {
    connectionListeners.add(setValue);
    return () => {
      connectionListeners.delete(setValue);
    };
  }, []);

  return value;
}
